import { useCallback, useEffect, useMemo } from 'react';
import { isEmpty, isNil, some, toString } from 'lodash';
import queryString, { ParsedQuery } from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Company } from 'api';
import { notFoundPath as NOT_FOUND_PAGE } from 'common/config/urls';
import { VERSION_QUERY_PARAM } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { ChildCompany, FirmCompany } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { useFundInvestmentContext } from 'context';
import { FundInvestmentsParams } from 'pages/FundInvestments';
import { FUND_INVESTMENTS_AVAILABLE_SLUG_PARAMS } from 'pages/FundInvestments/common/pages';
import { getArrayValue, getObjectValue } from 'utilities';

const useFundInvestmentRouteValidation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { companySlugParam: fundInvestmentSlugParam = '', tableSlugParam = '' } = useParams<FundInvestmentsParams>();

  const [storeValue] = useStore() as UseStoreValues;
  const { companyList, companyInfo: currentFundInvestment } = getObjectValue(storeValue);

  // Fund investments context
  const { fundInvestmentAllocationVersion } = useFundInvestmentContext();
  const { allocationVersions } = fundInvestmentAllocationVersion;

  const parsedQuery = useMemo<ParsedQuery<string>>(() => queryString.parse(location.search), [location]);
  const versionQueryParam = useMemo<string>(() => toString(parsedQuery?.[VERSION_QUERY_PARAM]), [parsedQuery]);

  const fundInvestmentsList = useMemo<FirmCompany[]>(
    () =>
      getArrayValue(
        companyList?.filter(({ company_type: companyType = '' }) => companyType === Company.type.INVESTMENT)
      ),
    [companyList]
  );

  const allFundInvestments = useMemo<(FirmCompany | ChildCompany)[]>(
    () =>
      getArrayValue([
        ...fundInvestmentsList,
        ...fundInvestmentsList.flatMap(fundInvestment => getArrayValue(fundInvestment?.child_companies)),
      ]),
  [fundInvestmentsList]
  );

  const validateAllocationVersionSlug = useCallback(() => {
    // If allocation versions loaded and version param is invalid
    if (!isNil(allocationVersions) && !some(allocationVersions, { slug: versionQueryParam })) {
      // Remove invalid version param from URL
      if (VERSION_QUERY_PARAM in parsedQuery) {
        delete parsedQuery[VERSION_QUERY_PARAM];

        navigate({ search: queryString.stringify(parsedQuery) }, { replace: true });
      }
    }
  }, [allocationVersions, navigate, parsedQuery, versionQueryParam]);

  const validateFundInvestmentSlug = useCallback(() => {
    if (!allFundInvestments.length || !fundInvestmentSlugParam) {
      return;
    }

    const foundFundInvestment = allFundInvestments.find(
      ({ company_slug: fundInvestmentSlug, company_id: fundInvestmentId }) =>
        fundInvestmentSlug === fundInvestmentSlugParam || fundInvestmentId === currentFundInvestment?.id
    );

    const isInvalidFundInvestment =
      !FUND_INVESTMENTS_AVAILABLE_SLUG_PARAMS.includes(tableSlugParam) ||
      isNil(foundFundInvestment) ||
      isEmpty(foundFundInvestment);

    if (isInvalidFundInvestment) {
      navigate(NOT_FOUND_PAGE);
    }

    validateAllocationVersionSlug();
  }, [
    allFundInvestments,
    currentFundInvestment?.id,
    fundInvestmentSlugParam,
    navigate,
    tableSlugParam,
    validateAllocationVersionSlug,
  ]);

  // Validate fund investment slug on mount
  useEffect(() => {
    validateFundInvestmentSlug();
  }, [validateFundInvestmentSlug]);
};

export default useFundInvestmentRouteValidation;
